import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { components } from "@dolfin/api-public";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { MODULE_FORBIDDEN_EVENT } from "@/lib/api-error";
import type { ModuleKey } from "@/lib/modules";

interface ModulesContextValue {
  /** Null while unknown: the lookup is in flight, or there is no client membership to read it from. */
  modules: string[] | null;
  loading: boolean;
  /**
   * Unknown reads as entitled, so a failed or pending lookup never grays out an owned module. The backend 403 is
   * the real enforcement. Given several keys, any one is enough.
   */
  hasModule: (key: ModuleKey | readonly ModuleKey[]) => boolean;
  /** Known to be held. For what is hidden, not grayed, without its module (Books): unknown reads as not held. */
  holdsModule: (key: ModuleKey) => boolean;
  /** Re-read the entitlements. Fired automatically after a Module.NotEntitled 403. */
  refresh: () => void;
}

const ModulesContext = createContext<ModulesContextValue | null>(null);

type Me = components["schemas"]["MeResponse"];

/** An org member gets the owning client's set; a client member with no org gets the client's. Null = unknown. */
function modulesFor(me: Me, organisationId: string | null, clientId: string | undefined): string[] | null {
  // `?? null` on both: a backend older than the field omits it, and that must read as unknown rather
  // than reach `hasModule` as undefined.
  const organisation = me.organisations.find((o) => o.id === organisationId);
  if (organisation) return organisation.modules ?? null;
  const client = me.clients.find((c) => c.id === clientId) ?? (me.clients.length === 1 ? me.clients[0] : undefined);
  return client?.modules ?? null;
}

/**
 * Read from GET /v1/me, not /v1/me/client (which refuses org-only users), so grants show on the next read.
 * A cache for graying out only: any Module.NotEntitled 403 invalidates it (MODULE_FORBIDDEN_EVENT).
 */
export function ModulesProvider({ children }: { children: ReactNode }) {
  const { token, user, activeOrganisationId } = useAuth();
  const [modules, setModules] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  // Read by the 403 listener, which runs outside React's render cycle and needs
  // the current values rather than the ones its closure was created with.
  const modulesRef = useRef<string[] | null>(null);
  const inFlightRef = useRef(false);

  // Picks the client for a client member who can see more than one. AuthContext
  // fills the active org's clientId in lazily from /v1/me, so it may be undefined
  // on the first pass and arrive on a later render.
  const clientId = user?.organisations.find(
    (o) => o.organisationId === activeOrganisationId,
  )?.clientId;

  useEffect(() => {
    if (!token) {
      modulesRef.current = null;
      setModules(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    inFlightRef.current = true;
    api
      .GET("/v1/me")
      .then(({ data, error }) => {
        if (cancelled) return;
        // A failed read leaves entitlements unknown, which `hasModule` treats as
        // "show everything".
        const next = error || !data ? null : modulesFor(data, activeOrganisationId, clientId);
        modulesRef.current = next;
        setModules(next);
      })
      .catch(() => {
        if (cancelled) return;
        modulesRef.current = null;
        setModules(null);
      })
      .finally(() => {
        inFlightRef.current = false;
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, activeOrganisationId, clientId, reloadCount]);

  const refresh = useCallback(() => setReloadCount((n) => n + 1), []);

  useEffect(() => {
    // One blocked page fires a 403 per request on it, so refetching on every
    // event would mean a burst of identical lookups. Skip the ones that tell us
    // nothing new: a module we already know is missing, or a refetch in flight.
    const onForbidden = (event: Event) => {
      const module = (event as CustomEvent<{ module: string | null }>).detail?.module;
      if (inFlightRef.current) return;
      if (module && modulesRef.current && !modulesRef.current.includes(module)) return;
      refresh();
    };
    window.addEventListener(MODULE_FORBIDDEN_EVENT, onForbidden);
    return () => window.removeEventListener(MODULE_FORBIDDEN_EVENT, onForbidden);
  }, [refresh]);

  const value = useMemo<ModulesContextValue>(
    () => ({
      modules,
      loading,
      hasModule: (key: ModuleKey | readonly ModuleKey[]) =>
        modules === null || (typeof key === "string" ? [key] : key).some((k) => modules.includes(k)),
      holdsModule: (key: ModuleKey) => modules !== null && modules.includes(key),
      refresh,
    }),
    [modules, loading, refresh],
  );

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
}

export function useModules(): ModulesContextValue {
  const context = useContext(ModulesContext);
  if (!context) throw new Error("useModules must be used within ModulesProvider");
  return context;
}
